/**
 * Pure, editor-free helpers for the block-level collab model used by
 * LexicalRichEditor. A "block" is one direct child of the Lexical root
 * (paragraph, heading, quote, list, code block, table — lists/tables/code are
 * single blocks in v1, not diffed internally). Everything here operates on
 * plain data ({id, node} pairs, snapshots, ops) so it can be unit-tested
 * without a live Lexical editor instance.
 *
 * Snapshot shape: { version: 1, blocks: [{ id, node }] }
 *   - id: stable block id, [A-Za-z0-9_-]{6,21}, lives OUTSIDE the node JSON.
 *   - node: the serialized Lexical node (one entry of
 *     editorState.toJSON().root.children — index parity is relied on by the
 *     caller when building a snapshot).
 *
 * Op shapes:
 *   { op: 'put', id, node, after: <id of preceding block | null> }
 *   { op: 'del', id }
 */

export const SNAPSHOT_VERSION = 1;

const ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/** Stable random block id: [A-Za-z0-9_-]{12}. Uses crypto.randomUUID when
 *  available (browser/Tauri/Node runtime), falls back to Math.random so this
 *  still works in plain test environments without a crypto global. */
export function generateBlockId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  let out = '';
  for (let i = 0; i < 12; i += 1) {
    out += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)];
  }
  return out;
}

/**
 * Block ids for a body that was stored WITHOUT ids (HTML only — what MCP, the
 * AI and imports write): derived from `seed` (the document id) and position,
 * so every teammate who opens the same body gets the SAME ids. With random
 * ids here each person's first edit named a block nobody else had, and every
 * live op landed as a NEW paragraph — duplicates on every screen
 * (e2e/demo/teamCollabDocs.check.js, 24.10.2026). Format stays
 * [A-Za-z0-9_-]{6,21}: "h" + 8 hash chars + "_" + base-36 index.
 */
export function seededBlockIds(seed, count) {
  let h = 0x811c9dc5;
  const s = String(seed);
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  const prefix = `h${h.toString(36).padStart(7, '0').slice(-8)}`;
  return Array.from({ length: count }, (_, i) => `${prefix}_${i.toString(36)}`);
}

export function makeSnapshot(blocks) {
  return { version: SNAPSHOT_VERSION, blocks: blocks || [] };
}

/**
 * Deterministic JSON stringify: recursively sorts object keys so two
 * semantically-identical snapshots/nodes produce byte-identical strings
 * regardless of source-side key insertion order. Array element order is
 * preserved (it's semantically significant — block order, node children
 * order) — only plain-object key order is normalized.
 *
 * Required because a block snapshot built fresh client-side (JS objects,
 * `{version, blocks}` insertion order preserved by spec) and the same
 * snapshot after a round trip through the backend's Jason-encoded encrypted
 * column (Elixir maps have no defined key order — Jason.decode!/encode!
 * commonly reorders to `{blocks, version}`) are NOT string-identical under
 * plain JSON.stringify even though they describe the same content. Any
 * equality/ring-buffer own-echo check comparing a locally-built snapshot
 * against one that came off the wire must go through this, not
 * JSON.stringify directly, or the comparison silently never matches.
 */
export function stableStringify(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => (v === undefined ? 'null' : stableStringify(v))).join(',')}]`;
  }
  const keys = Object.keys(value)
    .filter((k) => value[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

/**
 * Reconciles the NodeKey -> blockId map against the keys currently present in
 * the document (in document order). Keys already in `prevKeyToId` keep their
 * id (this is what makes split-paragraph id stability "just work": Lexical
 * keeps the original key on one half of the split, so it keeps its id; the
 * new half gets a fresh key and therefore a fresh id here). Keys no longer
 * present are dropped. New/unknown keys get a fresh id via `genId`.
 */
export function reconcileKeyMap(prevKeyToId, liveKeys, genId = generateBlockId) {
  const next = new Map();
  const prev = prevKeyToId instanceof Map ? prevKeyToId : new Map(Object.entries(prevKeyToId || {}));
  (liveKeys || []).forEach((key) => {
    next.set(key, prev.get(key) || genId());
  });
  return next;
}

/**
 * Diffs two block arrays ([{id, node}], ordered) into a list of ops.
 * - `del` for ids present in `prevBlocks` but absent from `nextBlocks`.
 * - `put` for ids that are new, or whose node JSON changed (compared via
 *   JSON.stringify), carrying the id of the block that now precedes it (or
 *   null if it's first).
 * Unchanged blocks (same JSON, regardless of neighbors) produce no op UNLESS
 * `options.detectReorder` is set, in which case a block whose position
 * relative to its still-present neighbors changed also gets a `put` (same
 * node JSON, corrected `after`) even though its content didn't change. This
 * is off by default for the incremental live-ops path (scheduleOpsEmit) —
 * every local edit re-diffing the whole document for pure reordering would
 * be noisy — but is turned on by `planApplyFullBlocks` for the authoritative
 * reconcile, where it repairs order drift from e.g. two peers concurrently
 * inserting a new block anchored at the same `after` id (each side would
 * otherwise permanently disagree on which insert comes first).
 * Returns [] when nothing changed.
 */
export function diffBlocks(prevBlocks, nextBlocks, options = {}) {
  const { detectReorder = false } = options;
  const prev = prevBlocks || [];
  const next = nextBlocks || [];
  // stableStringify: a record off the wire has its keys reordered (see above).
  const prevJsonById = new Map(prev.map((b) => [b.id, stableStringify(b.node)]));
  const prevIndexById = new Map(prev.map((b, i) => [b.id, i]));
  const nextIds = new Set(next.map((b) => b.id));
  const ops = [];

  prev.forEach((b) => {
    if (!nextIds.has(b.id)) ops.push({ op: 'del', id: b.id });
  });

  next.forEach((b, i) => {
    const prevJson = prevJsonById.get(b.id);
    const nextJson = stableStringify(b.node);
    const afterId = i > 0 ? next[i - 1].id : null;
    const contentChanged = prevJson === undefined || prevJson !== nextJson;
    let reordered = false;
    if (!contentChanged && detectReorder) {
      // Walk backward from this block's old position to find its nearest
      // still-present predecessor — if that id doesn't match its new
      // predecessor, the block moved even though its content didn't.
      const prevIdx = prevIndexById.get(b.id);
      let prevAfterId = null;
      for (let j = prevIdx - 1; j >= 0; j -= 1) {
        if (nextIds.has(prev[j].id)) {
          prevAfterId = prev[j].id;
          break;
        }
      }
      reordered = prevAfterId !== afterId;
    }
    if (contentChanged || reordered) {
      ops.push({ op: 'put', id: b.id, node: b.node, after: afterId });
    }
  });

  return ops;
}

/**
 * Applies `ops` to `currentBlocks` and returns the resulting plan:
 * { blocks: [{id, node}], skipped: [id, ...] }
 *
 * `options.protectedBlockId`, when set, causes any op targeting that block id
 * to be skipped instead of applied (skipped ids are returned so the caller
 * can record/log them — convergence is expected to come from a later
 * applyFullBlocks or subsequent ops, not a retry here).
 */
export function planApplyOps(currentBlocks, ops, options = {}) {
  const { protectedBlockId = null } = options;
  let blocks = (currentBlocks || []).map((b) => ({ id: b.id, node: b.node }));
  const skipped = [];

  (ops || []).forEach((op) => {
    if (protectedBlockId != null && op.id === protectedBlockId) {
      skipped.push(op.id);
      return;
    }
    if (op.op === 'del') {
      blocks = blocks.filter((b) => b.id !== op.id);
      return;
    }
    if (op.op === 'put') {
      const entry = { id: op.id, node: op.node };
      // Pull the block out of its current position first (if present) so a
      // `put` can also reposition it, not just replace its content in place —
      // needed for the reorder-repair ops `diffBlocks({ detectReorder: true })`
      // emits (see planApplyFullBlocks).
      const existingIdx = blocks.findIndex((b) => b.id === op.id);
      if (existingIdx !== -1) blocks.splice(existingIdx, 1);
      if (op.after != null) {
        const afterIdx = blocks.findIndex((b) => b.id === op.after);
        if (afterIdx !== -1) blocks.splice(afterIdx + 1, 0, entry);
        // Anchor not (yet) present: an existing block keeps its old spot
        // rather than being yanked to the end; a brand-new block falls back
        // to appending, same as before.
        else if (existingIdx !== -1) blocks.splice(existingIdx, 0, entry);
        else blocks.push(entry);
      } else if (existingIdx !== -1) {
        blocks.unshift(entry);
      } else {
        // New first-block insert: legacy quirk, preserved — appends rather
        // than unshifting (matches pre-existing behavior/tests).
        blocks.push(entry);
      }
    }
  });

  return { blocks, skipped };
}

/**
 * Authoritative reconcile: diffs `snapshot.blocks` against `currentBlocks` and
 * applies the result the same way `planApplyOps` does (same protected-block
 * skip semantics when focused). When there's no protected block (editor
 * unfocused), every op is applied — which naturally resolves to a full
 * replace when the two block lists have entirely disjoint ids.
 */
export function planApplyFullBlocks(currentBlocks, snapshot, options = {}) {
  const nextBlocks = (snapshot && snapshot.blocks) || [];
  const ops = diffBlocks(currentBlocks, nextBlocks, { detectReorder: true });
  return planApplyOps(currentBlocks, ops, options);
}

/**
 * COMMS-17: true when reconciling `snapshot` on top of `currentBlocks` would
 * skip an op targeting `protectedBlockId` — i.e. someone else's saved
 * version of the block the local caret is CURRENTLY in disagrees with local
 * content, so `planApplyFullBlocks`'s protected-block skip is about to favor
 * local content over theirs. That's the exact moment a same-block
 * concurrent-edit conflict is happening: harmless if the local edit hasn't
 * diverged (the incoming op is a no-op then anyway, since diffBlocks only
 * emits ops for actual changes), but a real one whenever it has — the
 * skipped update is later dropped for good (never replayed) the instant the
 * caret leaves the block with different content than it had at the skip
 * (see shouldReplaySkippedOp), silently discarding whichever author's save
 * loses the next server-side LWW race on this row (sync.ex's
 * merge_row/build_changes replace the whole bodyLexical field, not
 * per-block — see COMMS-17's evidence).
 *
 * Callers use this BEFORE handing `snapshot` to the live editor's
 * applyFullBlocks, to decide whether to surface a distinct "you're both
 * editing this paragraph" signal — mirrors the same skip condition
 * planApplyFullBlocks/planApplyOps apply internally (same diff options),
 * without needing a live editor instance to ask.
 */
export function reconcileConflictsWithBlock(currentBlocks, snapshot, protectedBlockId) {
  if (protectedBlockId == null) return false;
  const nextBlocks = (snapshot && snapshot.blocks) || [];
  const ops = diffBlocks(currentBlocks, nextBlocks, { detectReorder: true });
  return ops.some((op) => op.id === protectedBlockId);
}

/**
 * Decides whether a skipped op (one that targeted the block the local caret
 * was parked in, and so was queued instead of applied — see
 * LexicalRichEditor's skippedOpsMapRef/applySkippedOpsForBlock) should still
 * be replayed once the caret actually leaves that block.
 *
 * `baselineNodeJson` is the protected block's own serialized-node JSON
 * (JSON.stringify'd) captured at the moment the op was skipped.
 * `currentNodeJson` is the same block's serialized-node JSON recomputed right
 * before replay — or null/undefined if the block no longer exists (deleted
 * locally since the skip).
 *
 * Replays only when the block is byte-identical to the baseline, i.e. the
 * caret was merely parked there and nothing local changed it in the
 * meantime. If the user edited (or deleted) the block after the skip, the
 * queued op is stale — local wins, and it's dropped; convergence then comes
 * from the next authoritative reconcile of the remote author's own save
 * rather than from blindly replaying a now-outdated op over fresh local
 * text (this is what caused the truncation bug: a stale queued `put`
 * clobbering text typed after the skip).
 */
export function shouldReplaySkippedOp(baselineNodeJson, currentNodeJson) {
  if (currentNodeJson == null) return false; // block deleted locally since the skip
  if (baselineNodeJson == null) return false; // no baseline recorded — safest to drop
  return baselineNodeJson === currentNodeJson;
}

/**
 * Infers where a caret ended up after a text change, from the old and new
 * plain-text content of a block alone (no DOM, no op metadata) — used to make
 * a remote-collab presence avatar ride the just-typed text instead of waiting
 * for the next throttled presence offset push (see LexicalRichEditor's
 * applyRemoteOps/inferredCaretsRef).
 *
 * Computes the common-prefix length `p` and common-suffix length `s` between
 * `oldText` and `newText` (bounding `s` so `p + s` never exceeds the shorter
 * string's length — otherwise a short, single-character text could double-
 * count the same character as both prefix and suffix), and returns
 * `newText.length - s`: the end of the run that actually changed.
 *   - Pure append ("hi" -> "hi there"): s = 0 -> end of text.
 *   - Mid-insert ("ac" -> "abc"): s = 1 -> just past the inserted "b".
 *   - Deletion ("hello world" -> "hello"): s = 0 -> the deletion point.
 *   - Identical strings -> null (nothing changed, no caret to infer).
 */
export function inferCaretFromTextChange(oldText, newText) {
  const oldStr = oldText == null ? '' : oldText;
  const newStr = newText == null ? '' : newText;
  if (oldStr === newStr) return null;

  const maxCommon = Math.min(oldStr.length, newStr.length);
  let p = 0;
  while (p < maxCommon && oldStr[p] === newStr[p]) p += 1;

  const maxSuffix = maxCommon - p;
  let s = 0;
  while (
    s < maxSuffix &&
    oldStr[oldStr.length - 1 - s] === newStr[newStr.length - 1 - s]
  ) {
    s += 1;
  }

  return newStr.length - s;
}

/**
 * Parses each block's serialized node via `parseFn` (expected to be a Lexical
 * `$parseSerializedNode`-shaped callback, called from within editor context by
 * the caller). A block whose `parseFn` throws is dropped rather than crashing
 * the whole import; dropped ids are returned so the caller can console.warn.
 */
export function parseBlocksSafe(blocks, parseFn) {
  const parsed = [];
  const dropped = [];
  (blocks || []).forEach((b) => {
    try {
      const node = parseFn(b.node);
      parsed.push({ id: b.id, node });
    } catch (err) {
      dropped.push(b.id);
      // eslint-disable-next-line no-console
      console.warn(`[lexicalBlocks] dropped malformed block "${b.id}"`, err);
    }
  });
  return { parsed, dropped };
}
